import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useBranch } from '../contexts/BranchContext'
import { inventoryService } from '../services/firebase'
import { Package, Plus, Minus, AlertTriangle, Search, Edit, Trash2, ShoppingCart, TrendingUp, Filter } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { cn } from '../utils/cn'

export default function InventoryPage() {
  const { user } = useAuth()
  const { currentBranch } = useBranch()
  const [inventory, setInventory] = useState([])
  const [filteredInventory, setFilteredInventory] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showStockModal, setShowStockModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [stockOperation, setStockOperation] = useState({ type: 'add', quantity: 0, notes: '' })
  const [newItem, setNewItem] = useState({
    name: '',
    category: '',
    minStock: 10,
    currentStock: 0,
    unit: 'pcs',
    costPrice: 0,
    supplier: ''
  })
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [stockFilter, setStockFilter] = useState('all')

  const categories = ['Chemicals', 'Equipment', 'Supplies', 'Parts', 'Other']
  const units = ['pcs', 'liters', 'kg', 'bottles', 'boxes']

  useEffect(() => {
    loadInventory()
  }, [currentBranch])

  useEffect(() => {
    filterInventory()
  }, [inventory, searchTerm, categoryFilter, stockFilter])

  const loadInventory = async () => {
    if (!currentBranch) return
    
    try {
      setLoading(true)
      const inventoryData = await inventoryService.getInventoryByBranch(currentBranch.id)
      setInventory(inventoryData)
    } catch (error) {
      console.error('Error loading inventory:', error)
      toast.error('Failed to load inventory')
    } finally {
      setLoading(false)
    }
  }

  const filterInventory = () => {
    let filtered = inventory

    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.supplier?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => item.category === categoryFilter)
    }

    if (stockFilter !== 'all') {
      if (stockFilter === 'low') {
        filtered = filtered.filter(item => item.currentStock <= item.minStock)
      } else if (stockFilter === 'out') {
        filtered = filtered.filter(item => item.currentStock === 0)
      } else if (stockFilter === 'normal') {
        filtered = filtered.filter(item => item.currentStock > item.minStock)
      }
    }

    setFilteredInventory(filtered)
  }

  const handleAddItem = async (e) => {
    e.preventDefault()
    
    try {
      const itemData = {
        ...newItem,
        branchId: currentBranch.id,
        lowStock: newItem.currentStock <= newItem.minStock
      }
      
      await inventoryService.addInventoryItem(itemData)
      toast.success('Item added successfully')
      setShowAddModal(false)
      setNewItem({
        name: '',
        category: '',
        minStock: 10,
        currentStock: 0,
        unit: 'pcs',
        costPrice: 0,
        supplier: ''
      })
      loadInventory()
    } catch (error) {
      console.error('Error adding item:', error)
      toast.error('Failed to add item')
    }
  }

  const handleStockOperation = async (e) => {
    e.preventDefault()
    
    try {
      await inventoryService.updateStock(
        selectedItem.id, 
        parseInt(stockOperation.quantity), 
        stockOperation.type
      )
      
      // Add stock movement record
      await inventoryService.addStockMovement({
        itemId: selectedItem.id,
        branchId: currentBranch.id,
        type: stockOperation.type,
        quantity: parseInt(stockOperation.quantity),
        notes: stockOperation.notes,
        userId: user.uid
      })
      
      toast.success(`Stock ${stockOperation.type === 'add' ? 'added' : 'removed'} successfully`)
      setShowStockModal(false)
      setStockOperation({ type: 'add', quantity: 0, notes: '' })
      setSelectedItem(null)
      loadInventory()
    } catch (error) {
      console.error('Error updating stock:', error)
      toast.error('Failed to update stock')
    }
  }

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return
    
    try {
      await inventoryService.deleteItem(itemId)
      toast.success('Item deleted successfully')
      loadInventory()
    } catch (error) {
      console.error('Error deleting item:', error)
      toast.error('Failed to delete item')
    }
  }

  const getLowStockItems = () => {
    return inventory.filter(item => item.currentStock <= item.minStock)
  }

  const getTotalValue = () => {
    return inventory.reduce((total, item) => total + (item.currentStock * item.costPrice), 0)
  }

  const getUniqueCategories = () => {
    return [...new Set(inventory.map(item => item.category))].sort()
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 text-primary mb-4">
            <Package className="h-10 w-10" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            Inventory Management
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Track stock levels, manage items, and monitor inventory
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mr-4 text-primary">
                <Package className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Items</p>
                <p className="text-2xl font-bold text-gray-900">{inventory.length}</p>
              </div>
            </div>
          </div>
          
          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-rose-500/10 flex items-center justify-center mr-4 text-rose-600 dark:text-rose-400">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Low Stock</p>
                <p className="text-2xl font-bold text-gray-900">{getLowStockItems().length}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mr-4 text-emerald-700 dark:text-emerald-400">
                <ShoppingCart className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Value</p>
                <p className="text-2xl font-bold text-gray-900">R{getTotalValue().toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center mr-4 text-violet-700 dark:text-violet-400">
                <TrendingUp className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Categories</p>
                <p className="text-2xl font-bold text-gray-900">{getUniqueCategories().length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-center">
          <button
            onClick={() => setShowAddModal(true)}
            className="rounded-xl bg-primary px-8 py-4 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90"
          >
            <Plus className="h-5 w-5 inline mr-2 group-hover:rotate-90 transition-transform duration-300" />
            Add New Item
          </button>
        </div>

        {/* Filters */}
        <div className="rounded-xl border border-border/80 bg-card shadow-sm p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search inventory..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 appearance-none"
              >
                <option value="all">All Categories</option>
                {getUniqueCategories().map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            <div className="relative">
              <AlertTriangle className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={stockFilter}
                onChange={(e) => setStockFilter(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 appearance-none"
              >
                <option value="all">All Stock Levels</option>
                <option value="low">Low Stock</option>
                <option value="out">Out of Stock</option>
                <option value="normal">Normal Stock</option>
              </select>
            </div>
          </div>
        </div>

        {/* Inventory Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredInventory.map((item, index) => (
            <div 
              key={item.id}
              className="group relative rounded-xl border border-border/80 bg-card shadow-sm hover:shadow-xl transition-all duration-300 hover:scale-105 overflow-hidden"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Stock level indicator */}
              <div className={cn(
                'absolute top-4 right-4 w-3 h-3 rounded-full border-2 border-white shadow-md',
                item.currentStock <= item.minStock ? 'bg-red-400' : 
                item.currentStock <= item.minStock * 1.5 ? 'bg-yellow-400' : 'bg-green-400'
              )} />
              
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mr-3 text-primary">
                      <Package className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{item.name}</h3>
                      <p className="text-sm text-gray-600">{item.category}</p>
                    </div>
                  </div>
                </div>

                {/* Item Details */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">SKU:</span>
                    <span className="font-medium text-gray-900">{item.sku}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Unit:</span>
                    <span className="font-medium text-gray-900">{item.unit}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Price:</span>
                    <span className="font-medium text-gray-900">R{item.costPrice?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>

                {/* Stock Info */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="bg-primary/5 rounded-xl p-3 text-center">
                    <div className="text-lg font-bold text-blue-600">{item.currentStock}</div>
                    <div className="text-xs text-gray-600">In Stock</div>
                  </div>
                  <div className="bg-emerald-500/5 rounded-xl p-3 text-center">
                    <div className="text-lg font-bold text-green-600">R{(item.currentStock * item.costPrice).toFixed(2) || '0.00'}</div>
                    <div className="text-xs text-gray-600">Value</div>
                  </div>
                </div>

                {/* Stock Level Bar */}
                <div className="mb-6">
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                    <span>Stock Level</span>
                    <span>{Math.round((item.currentStock / item.minStock) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={cn(
                        'h-2 rounded-full transition-all duration-300',
                        item.currentStock <= item.minStock ? 'bg-red-500' : 
                        item.currentStock <= item.minStock * 1.5 ? 'bg-yellow-500' : 'bg-green-500'
                      )}
                      style={{ width: `${Math.min((item.currentStock / item.minStock) * 100, 100)}%` }}
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setSelectedItem(item)
                        setShowStockModal(true)
                      }}
                      className="p-2 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-xl transition-all duration-200"
                      title="Update Stock"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteItem(item.id)}
                      className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-xl transition-all duration-200"
                      title="Delete Item"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <span className={cn(
                    "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium",
                    item.currentStock <= item.minStock ? "bg-red-100 text-red-800" : 
                    item.currentStock <= item.minStock * 1.5 ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"
                  )}>
                    {item.currentStock <= item.minStock ? 'Low Stock' : 
                     item.currentStock <= item.minStock * 1.5 ? 'Warning' : 'In Stock'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredInventory.length === 0 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-muted rounded-3xl flex items-center justify-center mx-auto mb-6">
              <Package className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No inventory items found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || categoryFilter !== 'all' || stockFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by adding your first inventory item.'
              }
            </p>
            {!searchTerm && categoryFilter === 'all' && stockFilter === 'all' && (
              <button
                onClick={() => setShowAddModal(true)}
                className="rounded-xl bg-primary px-6 py-3 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90"
              >
                <Plus className="h-5 w-5 inline mr-2" />
                Add First Item
              </button>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mr-3 text-primary">
                  <Package className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Add New Item</h3>
              </div>
              
              <form onSubmit={handleAddItem} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Item Name *</label>
                  <input
                    type="text"
                    required
                    value={newItem.name}
                    onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  />
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">SKU *</label>
                    <input
                      type="text"
                      required
                      value={newItem.sku}
                      onChange={(e) => setNewItem({...newItem, sku: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Category *</label>
                    <input
                      type="text"
                      required
                      value={newItem.category}
                      onChange={(e) => setNewItem({...newItem, category: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity *</label>
                    <input
                      type="number"
                      required
                      min="0"
                      value={newItem.currentStock}
                      onChange={(e) => setNewItem({...newItem, currentStock: parseInt(e.target.value)})}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Min Stock *</label>
                    <input
                      type="number"
                      required
                      min="0"
                      value={newItem.minStock}
                      onChange={(e) => setNewItem({...newItem, minStock: parseInt(e.target.value)})}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Unit *</label>
                    <select
                      required
                      value={newItem.unit}
                      onChange={(e) => setNewItem({...newItem, unit: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    >
                      <option value="">Select Unit</option>
                      <option value="pieces">Pieces</option>
                      <option value="liters">Liters</option>
                      <option value="kilograms">Kilograms</option>
                      <option value="boxes">Boxes</option>
                      <option value="bottles">Bottles</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Cost Price (R) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={newItem.costPrice}
                    onChange={(e) => setNewItem({...newItem, costPrice: parseFloat(e.target.value)})}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  />
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 rounded-xl bg-primary py-3 px-4 font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90"
                  >
                    Add Item
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-2xl font-semibold hover:bg-gray-200 transition-all duration-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Stock Update Modal */}
      {showStockModal && selectedItem && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Update Stock: {selectedItem.name}
              </h3>
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  Current Stock: <span className="font-medium">{selectedItem.currentStock} {selectedItem.unit}</span>
                </p>
              </div>
              <form onSubmit={handleStockOperation} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Operation Type
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="add"
                        checked={stockOperation.type === 'add'}
                        onChange={(e) => setStockOperation({ ...stockOperation, type: e.target.value })}
                        className="mr-2"
                      />
                      <Plus className="h-4 w-4 text-green-600 mr-1" />
                      Add Stock
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="subtract"
                        checked={stockOperation.type === 'subtract'}
                        onChange={(e) => setStockOperation({ ...stockOperation, type: e.target.value })}
                        className="mr-2"
                      />
                      <Minus className="h-4 w-4 text-red-600 mr-1" />
                      Remove Stock
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={stockOperation.quantity}
                    onChange={(e) => setStockOperation({ ...stockOperation, quantity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    rows="3"
                    value={stockOperation.notes}
                    onChange={(e) => setStockOperation({ ...stockOperation, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Add notes about this stock movement..."
                  />
                </div>
                <div className="flex justify-end gap-2 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowStockModal(false)
                      setSelectedItem(null)
                      setStockOperation({ type: 'add', quantity: 0, notes: '' })
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 rounded-lg text-white ${
                      stockOperation.type === 'add' 
                        ? 'bg-green-600 hover:bg-green-700' 
                        : 'bg-red-600 hover:bg-red-700'
                    }`}
                  >
                    {stockOperation.type === 'add' ? 'Add Stock' : 'Remove Stock'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 